<template>
  <div class="min-w-[70vw]">
    <textarea
      ref="textarea"
      v-model="input"
      class="resize-none w-full border-primary-400 rounded-md p-4"
      placeholder="Type new comment..."
      tabindex="0"
      @keydown.enter.exact.prevent
      @keyup.enter.exact="submit"
    />

    <images-horizontal v-if="images.length" :images="images" :no-confirm="isEditMode" @delete="onFileDelete" />

    <files-horizontal v-if="files.length" :files="files" :no-confirm="isEditMode" @delete="onFileDelete" />

    <div class="flex">
      <div ref="hint" class="grow text-xs font-normal text-gray-400">
        Enter to send, Shift + Enter for newline
      </div>
      <file-uploader multiple @uploaded="onUpload">
        <button class="btn-outline mr-2">
          <Paperclip class="h-6 w-6 mr-2" aria-hidden="true" />
          Attach file
        </button>
      </file-uploader>
      <button class="btn-primary" @click="submit">
        <SendOutline class="h-6 w-6 mr-2" aria-hidden="true" />
        Send
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useTextareaAutosize } from '@vueuse/core';
import Paperclip from 'vue-material-design-icons/Paperclip.vue';
import SendOutline from 'vue-material-design-icons/SendOutline.vue';
import FileUploader from './FileUploader.vue';
import FilesHorizontal from './FilesHorizontal.vue';
import ImagesHorizontal from './ImagesHorizontal.vue';
import { $post, $put } from '../composables/useApi';
import { $error } from '../composables/useNotify';

const imagesExtensions = [ 'jpg', 'jpeg', 'png', 'gif', 'webp' ];

const { textarea, input } = useTextareaAutosize();

const props = defineProps({
  assetId: {
    type: String,
    default: null
  },
  data: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits([ 'update', 'close' ]);

const isEditMode = computed(() => Boolean(props.data?.comment?.id));

const urls = ref([]);
const files = computed(() => urls.value.filter(url => !imagesExtensions.includes(url.split('.').pop())).map(url => ({
  id: url,
  name: url.split('/').pop(),
  size: 0
})));
const images = computed(() => urls.value.filter(url => imagesExtensions.includes(url.split('.').pop())).map(url => ({
  id: url,
  url,
  size: 0
})));

const hint = ref(null);

function submit() {
  if (!input.value && !urls.value.length) {
    return;
  }

  if (isEditMode.value) {
    put();
  } else {
    post();
  }
}

async function post() {
  const text = input.value;

  const response = await $post(`/asset/${props.assetId?.replace('#', '')}/comments`, {
    text,
    files: urls.value
  });

  if (!response?.success) {
    $error('Error posting comment');
    console.error('Error posting comment:', response.error);
    return;
  }

  emit('update', { text, files: [ ...urls.value ] });
  input.value = '';
  urls.value = [];
}

async function put() {
  const text = input.value;

  const response = await $put(`/asset/comment/${props.data.comment.id}`, {
    text,
    files: urls.value
  });

  if (!response?.success) {
    $error('Error saving comment');
    console.error('Error saving comment:', response.error);
    return;
  }

  emit('close', true);
  input.value = '';
  urls.value = [];
}

function onUpload(_urls) {
  urls.value = [ ...urls.value, ..._urls ];
}

function onFileDelete(url) {
  urls.value = urls.value.filter(obj => obj !== url);
}

onMounted(() => {
  if (isEditMode.value) {
    textarea.value.focus();
    input.value = props.data.comment.text || '';
    urls.value = props.data.comment.files.map(file => file.id) || [];
    console.log('Comment data loaded:', props.data.comment.files);
  }
});
</script>
